import React, { useEffect, useRef, useState } from "react";
import { Typography, Image, Segmented, Button, Tooltip, Divider } from "antd";
import {
    LeftOutlined,
    WifiOutlined,
    BellOutlined,
    MobileOutlined,
    NotificationOutlined,
    RedoOutlined,
    LockOutlined,
    CameraOutlined,
    ThunderboltOutlined,
} from "@ant-design/icons";
import { formatMessageDate } from "../../../../../utils/Masks";
import { IMessage } from "../../../../../interfaces";
import logo from "../../../../../assets/images/logo.png";

interface IProps {
    theme: "light" | "dark";
    title: string;
    content?: string;
    message: IMessage | undefined;
    loggedInUserName: string;
    imageUrl: string | ArrayBuffer | null | undefined;
    body: string;
}

const { Title } = Typography;

export default function PhoneMockup({
    theme = "light",
    title,
    content = "",
    message,
    loggedInUserName,
    imageUrl,
    body,
}: IProps) {
    const isDark = theme === "dark";

    const [viewMode, setViewMode] = useState<"app" | "lockscreen">("app");
    const [showPushBanner, setShowPushBanner] = useState<boolean>(false);
    const [isAnimatingGesture, setIsAnimatingGesture] = useState<boolean>(false);

    const prevContentRef = useRef<string>(content);
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const triggerPushAnimation = () => {
        setIsAnimatingGesture(true);
        setShowPushBanner(true);

        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
        }

        setTimeout(() => {
            setIsAnimatingGesture(false);
        }, 700);

        hideTimeoutRef.current = setTimeout(() => {
            if (viewMode === "app") {
                setShowPushBanner(false);
            }
        }, 2000);
    };

    useEffect(() => {
        if (content && content !== prevContentRef.current) {
            if (!showPushBanner) {
                setIsAnimatingGesture(true);
                setShowPushBanner(true);
                setTimeout(() => {
                    setIsAnimatingGesture(false);
                }, 700);
            }

            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
            hideTimeoutRef.current = setTimeout(() => {
                if (viewMode === "app") {
                    setShowPushBanner(false);
                }
            }, 2000);
        }
        prevContentRef.current = content;
    }, [content, showPushBanner, viewMode]);

    useEffect(() => {
        if (viewMode === "lockscreen") {
            setShowPushBanner(true);
        }
    }, [viewMode]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                maxWidth: 360,
                margin: "0 auto",
                gap: 12,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "0 6px",
                    gap: 8,
                }}
            >
                <Segmented
                    value={viewMode}
                    onChange={(val) => setViewMode(val as "app" | "lockscreen")}
                    options={[
                        {
                            value: "app",
                            label: (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 500 }}>
                                    <MobileOutlined /> No App
                                </span>
                            ),
                        },
                        {
                            value: "lockscreen",
                            label: (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 500 }}>
                                    <NotificationOutlined /> Notificação Push
                                </span>
                            ),
                        },
                    ]}
                    size="small"
                />

                <Tooltip title="Disparar animação da notificação push descendo da barra superior">
                    <Button
                        size="small"
                        type="default"
                        icon={<RedoOutlined />}
                        onClick={() => {
                            if (showPushBanner) {
                                setShowPushBanner(false);
                                setTimeout(() => triggerPushAnimation(), 150);
                            } else {
                                triggerPushAnimation();
                            }
                        }}
                        style={{
                            fontSize: 11,
                            borderRadius: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontWeight: 500,
                        }}
                    >
                        Testar Push
                    </Button>
                </Tooltip>
            </div>

            <div
                style={{
                    width: 320,
                    height: 640,
                    borderRadius: 44,
                    backgroundColor: "#09090b",
                    padding: 10,
                    boxShadow: isDark
                        ? "0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.1)"
                        : "0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08)",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div
                    style={{
                        flex: 1,
                        borderRadius: 36,
                        overflow: "hidden",
                        backgroundColor:
                            viewMode === "lockscreen"
                                ? "#0f172a"
                                : isDark
                                    ? "#121212"
                                    : "#f8fafc",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                    }}
                >
                    <div
                        style={{
                            height: 40,
                            backgroundColor: viewMode === "lockscreen" ? "transparent" : "#1677ff",
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "0 20px",
                            fontSize: 11,
                            fontWeight: 600,
                            position: "relative",
                            zIndex: 40,
                            transition: "background-color 0.3s ease",
                        }}
                    >
                        <span>09:41</span>

                        <div
                            style={{
                                width: isAnimatingGesture ? 110 : 88,
                                height: 20,
                                backgroundColor: "#000000",
                                borderRadius: 20,
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                                top: 6,
                                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 6,
                                boxShadow: isAnimatingGesture ? "0 0 12px rgba(22, 119, 255, 0.5)" : "none",
                            }}
                        >
                            {isAnimatingGesture && (
                                <BellOutlined style={{ color: "#faad14", fontSize: 10, animation: "ringBell 0.4s infinite" }} />
                            )}
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <WifiOutlined style={{ fontSize: 11 }} />
                            <div
                                style={{
                                    width: 18,
                                    height: 9,
                                    border: "1px solid #ffffff",
                                    borderRadius: 3,
                                    padding: 1,
                                    display: "flex",
                                }}
                            >
                                <div
                                    style={{
                                        width: "70%",
                                        height: "100%",
                                        backgroundColor: "#ffffff",
                                        borderRadius: 1,
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {isAnimatingGesture && (
                        <div
                            style={{
                                position: "absolute",
                                top: 32,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 44,
                                height: 5,
                                borderRadius: 3,
                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                zIndex: 50,
                                animation: "gesturePull 0.7s ease-in-out",
                            }}
                        />
                    )}

                    {viewMode === "app" && (
                        <>
                            <div
                                style={{
                                    backgroundColor: "#1677ff",
                                    color: "#ffffff",
                                    padding: "6px 14px 12px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    position: "relative",
                                    zIndex: 10,
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <LeftOutlined style={{ fontSize: 12, color: "#fff", }} />
                                    <span style={{ fontSize: 14, fontWeight: 600 }}>Mensagem</span>
                                </div>
                            </div>
                            <div
                                style={{
                                    flex: 1,
                                    overflowY: "auto",
                                    padding: "16px 14px 24px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                }}
                            >

                                <Title
                                    level={5}
                                    style={{
                                        margin: 0,
                                        fontSize: 15,
                                        fontWeight: 700,
                                        color: isDark ? "#f1f5f9" : "#0f172a",
                                        lineHeight: 1.35,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {title || "Título da Mensagem"}
                                </Title>

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 6,
                                        marginTop: 0,
                                        fontSize: 11,
                                        color: isDark ? "#94a3b8" : "#64748b",
                                    }}
                                >
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                                        Por {message?.panel_user?.name || loggedInUserName}
                                    </span>
                                    <span>•</span>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                                        {formatMessageDate(
                                            message?.created_at
                                                ? new Date(message.created_at)
                                                : new Date()
                                        )}
                                    </span>
                                </div>

                                {imageUrl && typeof imageUrl === "string" && (
                                    <div
                                        style={{
                                            borderRadius: 14,
                                            overflow: "hidden",
                                            border: `1px solid ${isDark ? "#2d2d2d" : "#e2e8f0"}`,
                                            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                                        }}
                                    >
                                        <Image
                                            src={imageUrl}
                                            alt="Prévia do Anexo"
                                            preview={false}
                                            style={{
                                                width: "100%",
                                                display: "block",
                                                objectFit: "cover",
                                            }}
                                        />
                                    </div>
                                )}

                                <Divider style={{ padding: 0, margin: 4 }} />

                                <div
                                    style={{
                                        // backgroundColor: isDark ? "#1e1e1e" : "#ffffff",
                                        borderRadius: 14,
                                        padding: 2,
                                        // border: `1px solid ${isDark ? "#2d2d2d" : "#e2e8f0"}`,
                                        fontSize: 13,
                                        lineHeight: 1.6,
                                        color: isDark ? "#e2e8f0" : "#334155",
                                        wordBreak: "break-word",
                                        boxShadow: isDark
                                            ? "0 2px 8px rgba(0,0,0,0.3)"
                                            : "0 1px 4px rgba(0,0,0,0.04)",
                                    }}
                                    className="phone-mockup-body"
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            body && body.replace("<p></p>", "").trim().length > 0
                                                ? body
                                                : "<p style='color: #94a3b8; font-style: italic; margin: 0;'>O corpo da mensagem formatado aparecerá aqui conforme você digita no editor...</p>",
                                    }}
                                />
                            </div>
                        </>
                    )}

                    {viewMode === "lockscreen" && (
                        <div
                            style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                padding: "20px 14px 16px",
                                background: "linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)",
                                color: "#ffffff",
                                position: "relative",
                            }}
                        >
                            <div style={{ textAlign: "center", marginTop: 10 }}>
                                <LockOutlined style={{ fontSize: 14, color: "rgba(255, 255, 255, 0.75)" }} />
                                <div
                                    style={{
                                        fontSize: 54,
                                        fontWeight: 300,
                                        letterSpacing: -1,
                                        lineHeight: 1.1,
                                        marginTop: 4,
                                        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
                                    }}
                                >
                                    09:41
                                </div>
                                <div style={{ fontSize: 13, color: "rgba(255, 255, 255, 0.8)", fontWeight: 500, marginTop: 2 }}>
                                    Terça-feira, 15 de setembro
                                </div>
                            </div>

                            <div style={{ marginTop: 20, marginBottom: "auto" }}>
                                <PushNotificationCard
                                    title={title}
                                    content={content}
                                    imageUrl={imageUrl}
                                    isLockscreen={true}
                                />
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "0 10px",
                                    marginBottom: 10,
                                }}
                            >
                                <div
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: "50%",
                                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                                        backdropFilter: "blur(10px)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                    }}
                                >
                                    <ThunderboltOutlined style={{ fontSize: 16, color: "#fff" }} />
                                </div>

                                <div
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: "50%",
                                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                                        backdropFilter: "blur(10px)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CameraOutlined style={{ fontSize: 16, color: "#fff" }} />
                                </div>
                            </div>
                        </div>
                    )}

                    {viewMode === "app" && (
                        <div
                            style={{
                                position: "absolute",
                                top: 44,
                                left: 10,
                                right: 10,
                                zIndex: 100,
                                transform: showPushBanner ? "translateY(0) scale(1)" : "translateY(-140%) scale(0.92)",
                                opacity: showPushBanner ? 1 : 0,
                                pointerEvents: showPushBanner ? "auto" : "none",
                                transition: "transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease",
                            }}
                        >
                            <PushNotificationCard
                                title={title}
                                content={content}
                                imageUrl={imageUrl}
                                isLockscreen={false}
                                onDismiss={() => setShowPushBanner(false)}
                            />
                        </div>
                    )}

                    <div
                        style={{
                            height: 18,
                            backgroundColor: viewMode === "lockscreen" ? "transparent" : isDark ? "#121212" : "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            zIndex: 40,
                        }}
                    >
                        <div
                            style={{
                                width: 110,
                                height: 4,
                                backgroundColor: viewMode === "lockscreen" ? "rgba(255, 255, 255, 0.5)" : isDark ? "#4a4a4a" : "#cbd5e1",
                                borderRadius: 2,
                            }}
                        />
                    </div>
                </div>
            </div>

            <style>
                {`
                @keyframes gesturePull {
                    0% {
                        top: 15px;
                        opacity: 0.2;
                        transform: translateX(-50%) scaleX(0.7);
                    }
                    50% {
                        top: 55px;
                        opacity: 0.9;
                        transform: translateX(-50%) scaleX(1.3);
                    }
                    100% {
                        top: 75px;
                        opacity: 0;
                        transform: translateX(-50%) scaleX(1);
                    }
                }

                @keyframes ringBell {
                    0%, 100% { transform: rotate(0deg); }
                    25% { transform: rotate(15deg); }
                    75% { transform: rotate(-15deg); }
                }
                `}
            </style>
        </div>
    );
}

interface IPushCardProps {
    title: string;
    content: string;
    imageUrl?: string | ArrayBuffer | null;
    isLockscreen?: boolean;
    onDismiss?: () => void;
}

function PushNotificationCard({
    title,
    content,
    imageUrl,
    isLockscreen = false,
    onDismiss,
}: IPushCardProps) {
    return (
        <div
            onClick={onDismiss}
            style={{
                borderRadius: 18,
                padding: "12px 14px",
                background: isLockscreen
                    ? "rgba(30, 41, 59, 0.75)"
                    : "rgba(255, 255, 255, 0.92)",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                border: isLockscreen
                    ? "1px solid rgba(255, 255, 255, 0.15)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: isLockscreen
                    ? "0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.3)"
                    : "0 12px 30px -4px rgba(0, 0, 0, 0.22), 0 4px 10px -2px rgba(0, 0, 0, 0.1)",
                color: isLockscreen ? "#ffffff" : "#0f172a",
                cursor: "pointer",
                transition: "background 0.2s ease, border 0.2s ease",
                display: "flex",
                flexDirection: "column",
                gap: 6,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div
                        style={{
                            width: 20,
                            height: 20,
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ffffff",
                            fontSize: 10,
                            boxShadow: "0 2px 6px rgba(22, 119, 255, 0.4)",
                        }}
                    >
                        <Image
                            src={logo}
                            alt="Prévia do Anexo"
                            preview={false}
                            style={{
                                width: "100%",
                                display: "block",
                                objectFit: "cover",
                            }}
                        />
                    </div>

                    <span
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.4px",
                            textTransform: "uppercase",
                            color: isLockscreen ? "rgba(255, 255, 255, 0.9)" : "#334155",
                        }}
                    >
                        Nota Município
                    </span>
                </div>

                <span
                    style={{
                        fontSize: 10,
                        color: isLockscreen ? "rgba(255, 255, 255, 0.5)" : "#94a3b8",
                        fontWeight: 500,
                    }}
                >
                    agora
                </span>
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                    <div
                        style={{
                            fontSize: 13,
                            fontWeight: 700,
                            lineHeight: 1.3,
                            color: isLockscreen ? "#f8fafc" : "#0f172a",
                            wordBreak: "break-word",
                        }}
                    >
                        {title || "Título da Mensagem"}
                    </div>

                    <div
                        style={{
                            fontSize: 12,
                            lineHeight: 1.4,
                            color: isLockscreen ? "rgba(255, 255, 255, 0.8)" : "#475569",
                            wordBreak: "break-word",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {content || (
                            <span style={{ fontStyle: "italic", opacity: 0.6 }}>
                                O texto curto da notificação push aparecerá aqui em tempo real...
                            </span>
                        )}
                    </div>
                </div>
                {imageUrl && typeof imageUrl === "string" && (
                    <div
                        style={{
                            width: 42,
                            height: 42,
                            borderRadius: 8,
                            overflow: "hidden",
                            flexShrink: 0,
                            border: isLockscreen ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.1)",
                        }}
                    >
                        <img
                            src={imageUrl}
                            alt="thumb"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    </div>
                )}
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 2,
                }}
            >
                <div
                    style={{
                        width: 32,
                        height: 3,
                        borderRadius: 2,
                        backgroundColor: isLockscreen ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.15)",
                    }}
                />
            </div>
        </div>
    );
}
